import React, { useEffect } from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import {
  Button,
  InfoBar,
  isTouchDevice,
  type ManagedModalComponentProps,
  Modal,
  TextField,
} from "@shapr3d/frontend-lib";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { maxVersionNameLength } from "../../config";

import style from "./RenameVersionModal.module.css";

const schema = z.object({
  versionName: z.string().trim().min(1, "Please enter a version name").max(maxVersionNameLength),
});

type RenameVersionFormData = z.infer<typeof schema>;

interface RenameVersionModalProps {
  modalProps: ManagedModalComponentProps;
  versionName?: string;
  isPending?: boolean;
  isError?: boolean;
  onSave: (name: string) => void;
}

export const RenameVersionModal = React.forwardRef<HTMLElement, RenameVersionModalProps>(
  ({ modalProps, versionName, isPending = false, isError = false, onSave }, ref) => {
    const {
      register,
      handleSubmit,
      formState: { errors, isSubmitting },
      reset,
    } = useForm<RenameVersionFormData>({
      resolver: zodResolver(schema),
      defaultValues: { versionName: versionName },
    });

    useEffect(() => {
      if (modalProps.open) {
        reset({ versionName: versionName });
      }
    }, [modalProps.open, versionName, reset]);

    const submit = (data: RenameVersionFormData) => {
      onSave(data.versionName);
    };

    const isSaving = isPending || isSubmitting;

    return (
      <Modal {...modalProps} size="medium" title={"Rename this version"} ref={ref}>
        <form className={style.renameVersionForm} onSubmit={handleSubmit(submit)}>
          <TextField
            {...register("versionName")}
            placeholder={"Version name"}
            errorMessage={errors.versionName?.message}
            maxLength={maxVersionNameLength}
            disabled={isSaving}
            autoFocus={!isTouchDevice()}
            autoComplete="off"
          />
          {isError && (
            <InfoBar variant="critical" title="Something went wrong. Please try again." />
          )}
          <div className={style.actions}>
            <Button variant="grayscale" onClick={modalProps.onClose} disabled={isSaving}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSaving}>
              Save
            </Button>
          </div>
        </form>
      </Modal>
    );
  }
);

RenameVersionModal.displayName = "RenameVersionModal";
